import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader, StatusPill } from "@/components/common";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import LeadFormDialog from "./LeadFormDialog";

const RowActions = ({ l, act, onEdit }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`lead-actions-${l.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onEdit} data-testid={`lead-edit-${l.id}`}>Edit</DropdownMenuItem>
      {l.verification_status !== "verified" && <DropdownMenuItem onClick={() => act("verify", { status: "verified" }, "Lead verified")} data-testid={`lead-verify-${l.id}`}>Mark verified</DropdownMenuItem>}
      {l.verification_status !== "rejected" && <DropdownMenuItem onClick={() => act("verify", { status: "rejected" }, "Lead rejected")} data-testid={`lead-reject-${l.id}`}>Reject</DropdownMenuItem>}
      {l.published ? <DropdownMenuItem onClick={() => act("publish", { published: false }, "Lead unpublished")} data-testid={`lead-unpublish-${l.id}`}>Unpublish</DropdownMenuItem>
        : <DropdownMenuItem disabled={l.verification_status !== "verified"} onClick={() => act("publish", { published: true }, "Lead published")} data-testid={`lead-publish-${l.id}`}>Publish</DropdownMenuItem>}
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive" onClick={() => window.confirm("Delete this lead permanently?") && act("delete", null, "Lead deleted")} data-testid={`lead-delete-${l.id}`}>Delete</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default function AdminLeads() {
  const [q, setQ] = useState({ search: "", status: "all", published: "all" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ open: false, lead: null });

  const load = () => {
    const params = { page, search: q.search };
    if (q.status !== "all") params.status = q.status;
    if (q.published !== "all") params.published = q.published;
    return api.get("/admin/leads", { params }).then((r) => setData(r.data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page]);

  const act = (id) => async (kind, body, msg) => {
    try {
      if (kind === "delete") await api.delete(`/admin/leads/${id}`);
      else await api.post(`/admin/leads/${id}/${kind}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Inventory" title="Lead management" subtitle="Add, verify and publish leads. Contacts are stored privately and never exposed to agencies without entitlement."
        actions={<Button onClick={() => setEdit({ open: true, lead: null })} data-testid="add-lead-button"><Plus className="h-4 w-4 mr-2" />Add lead</Button>} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search title, category, country" value={q.search} data-testid="admin-leads-search" onChange={(e) => { setQ({ ...q, search: e.target.value }); setPage(1); }} /></div>
        <Select value={q.status} onValueChange={(v) => { setQ({ ...q, status: v }); setPage(1); }}>
          <SelectTrigger className="sm:w-44" data-testid="admin-leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="verified">Verified</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Select value={q.published} onValueChange={(v) => { setQ({ ...q, published: v }); setPage(1); }}>
          <SelectTrigger className="sm:w-44" data-testid="admin-leads-published-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All visibility</SelectItem><SelectItem value="true">Published</SelectItem><SelectItem value="false">Unpublished</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="bg-card rounded-xl border overflow-x-auto fade-up">
        <Table data-testid="admin-leads-table">
          <TableHeader><TableRow><TableHead>Lead</TableHead><TableHead>Client</TableHead><TableHead>Budget</TableHead><TableHead>Cost</TableHead><TableHead>Status</TableHead><TableHead>Visibility</TableHead><TableHead>Unlocks</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((l) => (
              <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                <TableCell className="min-w-[240px]"><p className="font-medium">{l.title}</p><p className="text-xs text-muted-foreground">{l.category} · {l.country} · {fmtDate(l.created_at)}</p></TableCell>
                <TableCell className="min-w-[180px] text-sm"><p>{l.contact?.client_name}</p><p className="text-xs text-muted-foreground">{l.contact?.email || l.contact?.phone}</p></TableCell>
                <TableCell className="whitespace-nowrap">{fmtMoney(l.budget, l.currency)}</TableCell>
                <TableCell className="font-mono">{l.credit_cost}</TableCell>
                <TableCell><StatusPill status={l.verification_status} testId={`admin-lead-status-${l.id}`} /></TableCell>
                <TableCell><StatusPill status={l.published ? "published" : "cancelled"} testId={`admin-lead-published-${l.id}`} /></TableCell>
                <TableCell className="font-mono">{l.unlock_count}</TableCell>
                <TableCell><RowActions l={l} act={act(l.id)} onEdit={() => setEdit({ open: true, lead: l })} /></TableCell>
              </TableRow>
            ))}
            {data && data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No leads found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {data?.pages > 1 && (
        <div className="flex items-center justify-end gap-3 mt-4">
          <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-mono">{page} / {data.pages}</span>
          <Button variant="outline" size="icon" disabled={page >= data.pages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
      <LeadFormDialog open={edit.open} lead={edit.lead} onOpenChange={(o) => setEdit({ open: o, lead: o ? edit.lead : null })} onSaved={load} />
    </div>
  );
}
